import React, { Component } from 'react';
import _throttle from 'lodash.throttle';
import {
  CreditsInfo,
  HomePageHeader,
  Icon,
  List,
  ElevatedContainer,
  Text,
  Error,
  notify,
  PaymentSummary,
  AddonsProvider,
  SubscriptionInfoBox,
  GetInTouchModal,
  PublicUploadModal,
  AnalyticsBanner,
  NUMERIC_MEDIA_SIZES,
  Tabs,
  isShootstaRole,
  hasPermission
} from '@shootsta/common-react';
import cx from 'classnames';
import _memoize from 'lodash.memoize';
import _debounce from 'lodash.debounce';
import _isEqual from 'lodash.isequal';
import _uniqBy from 'lodash.uniqby';
import type { RouterHistory } from '@core/shared';
import { replaceBucketKeysWithUrlsInArray } from '@shootsta/client-aws-s3';
import { fetchAssetsFromVideos } from '@shootsta/video-assets';
import moment from 'moment';
import { PRODUCT_TYPE } from '@shootsta/common-domain-data';
import { credentials } from '@shootsta/client-auth';
import TopupCreditsModal from '../../TopupCreditsModal';
import { openHubSpotConversationsWidget } from '../../../hubSpot';

import { VideoShareModal } from '../../VideoShareModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ArchiveModal from './ArchiveModal';
import DuplicateModal from './DuplicateModal';
import RenameConfirmationModal from './RenameConfirmationModal';
import resolveProfileImages from '../utils/resolveProfileImages';
import GettingStartedSteps from './GettingStartedSteps';
import InformationSteps from './InformationSteps';
import getBasePathByProductType from '../utils/getBasePathByProductType';
import elevatePath from '../constants/elevatePath';
import ProjectListItemV1 from './ProjectListItemV1';
import ProjectListItemV2 from './ProjectListItemV2';
import '../styles/_video-list.scss';

const CURRENT_VIDEOS_LENGTH_WITHOUT_STARTED_STEPS = 5;
const DEFAULT_PROJECT_NAME = 'Untitled Project';

const ADVANCED_FILTERS = (
  searchOptions: User[],
  canViewOwnedByEveryone: boolean,
  lockStatus = false
): any[] => [
  {
    type: 'MULTI_SEARCH',
    name: 'USER',
    label: 'User',
    additionalOptions: {
      placeholder: 'Search users',
      customPrefix: 'search',
      searchOptions
    },
    filterFacets: [
      { label: 'Collaborating On', value: 'COLLABORATING_ON' },
      { label: 'Owned by Me', value: 'ME', selectedDefault: true },
      ...(canViewOwnedByEveryone
        ? [{ label: 'Owned by Everyone', value: 'ALL' }]
        : [])
    ]
  },
  {
    type: 'DATE_RANGE',
    name: 'DATE_CREATED',
    label: 'Date Created',
    filterFacets: [
      { label: 'All', value: 'ALL', selectedDefault: true },
      {
        label: 'Date Range',
        value: 'DATE_RANGE',
        showDateRange: true,
        startPlaceholder: 'Select date',
        endPlaceholder: 'Select date'
      }
    ]
  },
  // In a status-scoped view (e.g. Completed Projects) the STATUS filter is
  // fixed, so the facet is omitted to avoid letting users break the scope.
  ...(lockStatus
    ? []
    : [
        {
          type: 'RADIO',
          name: 'STATUS',
          label: 'Project Status',
          filterFacets: [
            { label: 'All Videos', value: 'ALL', selectedDefault: true },
            { label: 'Draft', value: 'DRAFT' },
            { label: 'In Progress', value: 'IN_PROGRESS' },
            { label: 'Requires Action', value: 'REQUIRES_ACTION' },
            { label: 'On Hold', value: 'ON_HOLD' },
            { label: 'Complete', value: 'COMPLETE' }
          ]
        }
      ]),
  {
    type: 'RADIO',
    name: 'isArchived',
    label: 'Status',
    filterFacets: [
      { label: 'Active', value: false, selectedDefault: true },
      { label: 'Archived', value: true }
    ]
  }
];

const SORT_FILTERS = [
  { label: 'Name: A - Z', value: 'aToZ' },
  { label: 'Name: Z - A', value: 'zToA' },
  { label: 'Date: Old - New', value: 'dateOldToNew' },
  { label: 'Date: New - Old', value: 'dateNewToOld', selectedDefault: true }
];

const SORT_FILTERS_V5 = [
  { label: 'Last Updated', value: 'dateNewToOld', selectedDefault: true },
  { label: 'First Updated', value: 'dateOldToNew' },
  { label: 'Name: A - Z', value: 'aToZ' },
  { label: 'Name: Z - A', value: 'zToA' }
];

const CHUNK_SIZE = 16;

type VideoListProps = {
  history: RouterHistory;
  fetchAssets: any;
  getFilteredVideos: any;
  getFilteredEvents: any;
  createHistoryEntries: any;
  getVideoCountCreatedByUser: any;
  getOutputs: any;
  getUsers: any;
  getCreditsConsumption: any;
  containerSize: Size;
  deleteVideo: any;
  videoProjectId?: string;
  headerTitle: string;
  headerSubtitle: string;
  headerIcon?: string;
  headerIconColor?: string;
  headerSecondaryContent?: Node;
  headerSubtitleLink?: string;
  headerBreadcrumbs?: any[];
  apiClient: any;
  organisationId: string;
  subPackage?: any;
  isAdmin: boolean;
  product?: string;
  globalStore: any;
  flags: any;
  organisationMemberships: any[];
  canCreateOrSubmitProject: boolean;
  createVideo: any;
  tabs?: any;
  getVideoPaymentStatus?: any;
  openInvoices: any;
  isShootstaUser: boolean;
  permissions: string[];
  canViewOwnedByEveryone: boolean;
  clientType: string;
  selectedOrganisation?: any;
  getInfiniteScrollParent?: () => Node | null;
  initialStatusFilter?: string[];
  lockStatusFilter?: boolean;
};

type VideoListState = {
  error: boolean;
  loading?: boolean;
  stats?: OutputStats;
  searchTerm?: string;
  sortBy?: string;
  sortByLabel?: string;
  count?: number;
  isGettingStartedSteps?: boolean;
  videos?: Video[];
  users: User[];
  offset: number;
  videoToView?: Video;
  videoToDelete?: Video;
  videoToRename?: Video;
  videoToDuplicate?: Video;
  videoToArchive?: Video;
  videoToOpenCostSummary?: Video;
  videoToPublicUpload?: Video;
  shareableVideo: Video;
  selectedFilters: any;
  isGetInTouchModalOpen: boolean;
  videoCountCreatedByUser?: number;
  isCreateProjectDisabled: boolean;
  currentVersionConfig: any;
  tabs?: any;
  showTopUpCreditModal: boolean;
  newShareModalVisible: boolean;
};

function getShareUrlForVideo(video: Video) {
  if (!video) {
    return '';
  }

  const { serviceType } = video;

  const basePath = getBasePathByProductType(video.product);

  return `${window.location.origin}${basePath}${
    serviceType && elevatePath === basePath ? '/v2' : ''
  }/project/${video.videoId}`;
}

const defaultVersion = {
  isEnabled: true,
  listItemComponent: ProjectListItemV1,
  listProps: {}
} as const;

class ProjectList extends Component<VideoListProps, VideoListState> {
  _dashboardRef: any;

  _currentRequestId: number;

  _pageYOffset: number;

  isPro: boolean;

  isElevate: boolean;

  setStateAndPersist = (state: any, callback?: any) => {
    const {
      globalStore: { updateObjectForKey },
      history
    } = this.props;

    const {
      location: { pathname }
    } = history;

    this.setState(state, () => {
      // Save state
      updateObjectForKey(pathname, {
        ...state
      });
      if (callback) {
        callback();
      }
    });
  };

  saveScroll = () => {
    const {
      globalStore: { updateObjectForKey },
      history
    } = this.props;

    const {
      location: { pathname }
    } = history;

    const scrollElement = this.getVideoListRef();

    if (scrollElement) {
      const { scrollTop } = scrollElement;

      updateObjectForKey(pathname, {
        pageYOffset: scrollTop
      });
    }
  };

  openDeleteModal = _memoize((videoToDelete: Video) => async () => {
    this.setStateAndPersist({ videoToDelete });
  });

  openProjectCostSummaryModal = _memoize(
    (videoToOpenCostSummary: Video) => async () => {
      const { videoId } = videoToOpenCostSummary;

      const { outputs } = await this.fetchProjectOutputs(videoId);
      const { getVideoPaymentStatus, flags } = this.props;
      const { enabledPaymentGateway } = flags || {};

      if (!enabledPaymentGateway || !getVideoPaymentStatus) {
        this.setStateAndPersist({
          videoToOpenCostSummary: {
            ...videoToOpenCostSummary,
            outputs
          }
        });
        return;
      }

      const { data, error } = await getVideoPaymentStatus({ videoId });

      if (!data || error) {
        this.setStateAndPersist({
          videoToOpenCostSummary: {
            ...videoToOpenCostSummary,
            outputs
          }
        });
        return;
      }

      const { getVideoPaymentStatus: videoPaymentStatusData } = data;

      const { status = '' } = videoPaymentStatusData || {};

      this.setStateAndPersist({
        videoToOpenCostSummary: {
          ...videoToOpenCostSummary,
          outputs,
          paymentStatus: status
        }
      });
    }
  );

  openDuplicateModal = _memoize((videoToDuplicate: Video) => async () => {
    this.setStateAndPersist({ videoToDuplicate });
  });

  openArchiveModal = _memoize((videoToArchive: Video) => async () => {
    this.setStateAndPersist({ videoToArchive });
  });

  openRenameModal = _memoize((videoToRename: Video) => async () => {
    this.setStateAndPersist({ videoToRename });
  });

  openPublicUploadModal = _memoize((videoToPublicUpload: Video) => async () => {
    this.setStateAndPersist({ videoToPublicUpload });
  });

  openGetInTouchModal = () => {
    this.setStateAndPersist({ isGetInTouchModalOpen: true });
  };

  constructor(props: VideoListProps) {
    super(props);

    const {
      globalStore: { getObjectForKey },
      history,
      product,
      initialStatusFilter
    } = props;

    const {
      location: { pathname }
    } = history;

    // Restore saved scroll position and state from store and merge with default state
    const { pageYOffset, ...savedState } = getObjectForKey(pathname) || {};
    this._pageYOffset = pageYOffset || 0;

    // Restore saved state from store and merge with default state
    this.state = {
      error: false,
      // Seed the status filter for status-scoped views (e.g. Completed
      // Projects). Saved state (spread below) still wins on return visits.
      selectedFilters: initialStatusFilter
        ? { STATUS: initialStatusFilter }
        : {},
      offset: 0,
      isGetInTouchModalOpen: false,
      users: [],
      isCreateProjectDisabled: false,
      currentVersionConfig: defaultVersion,
      tab: 0,
      tabMetaData: {
        inDiscussion: {
          videoIds: [],
          count: 0,
          isUnread: false
        },
        signOff: {
          videoIds: [],
          count: 0,
          isUnread: false
        }
      },
      showTopUpCreditModal: false,
      newShareModalVisible: false,
      ...savedState
    };

    this.isPro = !product || product === PRODUCT_TYPE.PRO;
    this.isElevate = product === PRODUCT_TYPE.PITCHTEMPLATE;
  }

  componentDidMount() {
    window.addEventListener('scroll', _debounce(this.saveScroll, 100), true);
    this.fetchUsers();
    this.fetchVideoCountCreatedByUser();
    this.setCurrentVersionConfig();

    const scrollElement = this.getVideoListRef();

    if (scrollElement) {
      setTimeout(() => {
        scrollElement.scrollTop = this._pageYOffset;
      }, 200);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.saveScroll);
  }

  getWorkflowV5TabsVisibility = () => {
    const { isShootstaUser, isAdmin, selectedOrganisation } = this.props;

    if (isShootstaUser || isAdmin) return true;

    return Boolean(selectedOrganisation?.user?.permissions?.length);
  };

  syncUnreadVideoCount = async () => {
    const { getFilteredEvents } = this.props;
    const { tabMetaData } = this.state;
    const { inDiscussion, signOff } = tabMetaData;

    const {
      data: { getFilteredEvents: filteredEvents }
    } = await getFilteredEvents({
      eventTypes: ['SIGN_OFF_PROJECT_READ', 'IN_DISCUSSION_PROJECT_READ'],
      createdByUserId: true
    });

    const eventsGroupedByType = filteredEvents.reduce<Record<string, any>>(
      (acc, event) => {
        if (!acc[event.eventType]) {
          acc[event.eventType] = [];
        }
        acc[event.eventType].push(event);
        return acc;
      },
      {}
    );

    const videoIdsGroupedByEventType = {
      SIGN_OFF_PROJECT_READ: [],
      IN_DISCUSSION_PROJECT_READ: []
    } as const;

    for (const [eventType, events] of Object.entries(eventsGroupedByType)) {
      videoIdsGroupedByEventType[eventType] = [
        ...new Set(events.map((event) => event.resourceId))
      ];
    }

    this.setState({
      tabMetaData: {
        inDiscussion: {
          ...inDiscussion,
          isUnread:
            inDiscussion.count !==
            videoIdsGroupedByEventType?.IN_DISCUSSION_PROJECT_READ.length
        },
        signOff: {
          ...signOff,
          isUnread:
            signOff.count !==
            videoIdsGroupedByEventType?.SIGN_OFF_PROJECT_READ.length
        }
      }
    });
  };

  setCurrentVersionConfig() {
    const { flags } = this.props;
    const { videoWorkflowV2 = false, workflowV5 = false } = flags || {};

    const versionConfigs = [
      {
        isEnabled: !videoWorkflowV2,
        listItemComponent: ProjectListItemV1,
        listProps: {}
      },
      {
        isEnabled: videoWorkflowV2,
        listItemComponent: ProjectListItemV2,
        listProps: {
          isSearchBarCollapsed: true,
          alignSearchRight: true,
          sortFilters: workflowV5 ? SORT_FILTERS_V5 : null
        }
      }
    ];

    const enabledVersions = versionConfigs.filter((config) => config.isEnabled);
    this.setState({
      currentVersionConfig:
        enabledVersions[enabledVersions.length - 1] || defaultVersion
    });
  }

  componentDidUpdate(nextProps: VideoListProps, nextState: VideoListState) {
    const { flags, tabs } = this.props;
    const { selectedFilters, tab, tabMetaData } = this.state;
    const { flags: nextFlags, tabs: prevTabs } = nextProps;
    const updatedTabs = tabs
      ? {
          ...tabs,
          [tab]: {
            ...tabs[tab],
            selectedFilters
          }
        }
      : undefined;
    if (!_isEqual(tabs, prevTabs)) {
      this.setStateAndPersist(
        {
          tabs: updatedTabs,
          offset: 0
        },
        this.fetchData
      );
    }

    if (
      flags?.workflowV5 &&
      (!_isEqual(
        nextState.tabMetaData.inDiscussion,
        tabMetaData.inDiscussion
      ) ||
        !_isEqual(nextState.tabMetaData.signOff, tabMetaData.signOff))
    ) {
      this.syncUnreadVideoCount();
    }

    if (_isEqual(flags, nextFlags)) {
      return;
    } else {
      this.fetchData();
    }
    this.setCurrentVersionConfig();
  }

  onSearchFilterChange = ({ searchTerm }: any) => {
    const { tabs, tab } = this.state;

    const tabsSearchTerm =
      tabs && tabs[tab]
        ? {
            ...tabs,
            [tab]: {
              ...tabs[tab],
              searchTerm: searchTerm
            }
          }
        : undefined;

    this.setStateAndPersist(
      {
        searchTerm,
        tabs: tabsSearchTerm,
        offset: 0
      },
      this.fetchData
    );
  };

  onSortFiltersChange = (sortBy: any) => {
    const { tabs, tab } = this.state;
    const updatedTabs = tabs
      ? {
          ...tabs,
          [tab]: {
            ...tabs[tab],
            sortBy: sortBy.value,
            sortByLabel: sortBy.label
          }
        }
      : undefined;

    this.setStateAndPersist(
      {
        sortBy: sortBy.value,
        sortByLabel: sortBy.label,
        offset: 0,
        tabs: updatedTabs
      },
      this.fetchData
    );
  };

  fetchAssetsFromVideos = (videos: Video[]) => {
    const { fetchAssets } = this.props;

    return fetchAssetsFromVideos({ videos, fetchAssets, isOptimized: true });
  };

  onUpdateFilters = (selectedFilters: any) => {
    const { tab, tabs } = this.state;
    const selectedTabFilters =
      tabs && tabs[tab]
        ? {
            ...tabs,
            [tab]: {
              ...tabs[tab],
              selectedFilters: selectedFilters
            }
          }
        : undefined;
    this.setStateAndPersist(
      {
        selectedFilters,
        tabs: selectedTabFilters,
        offset: 0
      },
      this.fetchData
    );
  };

  fetchProjectOutputs = async (videoId: string) => {
    const { getOutputs } = this.props;
    const data = await getOutputs({ videoId });

    return data.data.getOutputsSummary;
  };

  fetchUsers = async () => {
    const { getUsers, organisationId, apiClient } = this.props;

    try {
      const { data, error } = await getUsers();

      if (error || !data) {
        return void this.setStateAndPersist({ error: true });
      }

      const { getUsers: userData } = data;

      const users = await replaceBucketKeysWithUrlsInArray({
        apiClient,
        dataArray: userData.map(
          ({ firstName, lastName, userId, imageUrl }) => ({
            label: `${firstName} ${lastName}`,
            value: userId,
            imageUrl
          })
        ),
        organisationId,
        substitutedKeys: ['imageUrl']
      });

      this.setStateAndPersist({ users });
    } catch (err: any) {
      return void this.setStateAndPersist({ error: true });
    }
  };

  fetchVideoCountCreatedByUser = async () => {
    const { getVideoCountCreatedByUser } = this.props;

    try {
      const { data, error } = await getVideoCountCreatedByUser();

      if (error || !data) {
        return void this.setStateAndPersist({ error: true });
      }

      const { getVideoCount: count } = data;

      this.setStateAndPersist({ videoCountCreatedByUser: count });
    } catch (err: any) {
      return void this.setStateAndPersist({ error: true });
    }
  };

  // Status-scoped views (e.g. Completed Projects) hide the STATUS facet, so the
  // AdvancedFilters component emits filter sets without it — both on its initial
  // update and whenever another filter changes. Force the locked status into
  // every query so the scope can never be lost.
  applyLockedStatusFilter = (selectedFilters: any) => {
    const { lockStatusFilter, initialStatusFilter } = this.props;
    return lockStatusFilter && initialStatusFilter
      ? { ...selectedFilters, STATUS: initialStatusFilter }
      : selectedFilters;
  };

  fetchData = async () => {
    const {
      getFilteredVideos,
      videoProjectId,
      apiClient,
      product,
      flags: { workflowV5 }
    } = this.props;

    this.setState({
      loading: true
    });

    const {
      searchTerm,
      selectedFilters: selectedFiltersState,
      tabs,
      sortBy,
      tab,
      videos: prevVideos
    } = this.state;
    const requestId = new Date().getTime();
    this._currentRequestId = requestId;
    const selectedFilters = this.applyLockedStatusFilter(
      tabs ? tabs[tab].selectedFilters : selectedFiltersState
    );
    const sort = workflowV5 ? SORT_FILTERS_V5[0] : SORT_FILTERS[3];
    const { data, error } = await getFilteredVideos({
      ...selectedFilters,
      searchTerm: tabs ? tabs[tab].searchTerm : searchTerm,
      limit: CHUNK_SIZE,
      offset: 0,
      sortBy: (tabs ? tabs[tab].sortBy : sortBy) || sort.value,
      videoProjectId,
      product: this.isPro ? PRODUCT_TYPE.PRO : product
    });

    this.setState({
      loading: false
    });

    // Ignore any but the most recent request
    if (requestId !== this._currentRequestId) {
      return;
    }

    if (!data || error || !data.getFilteredVideos) {
      return void this.setStateAndPersist({ loading: false, error: true });
    }

    const { count, videos, videosInDiscussion, videosInSignOff } =
      data.getFilteredVideos;

    this.fetchAssetsFromVideos(videos);

    const withCreatorProfileImages = await resolveProfileImages(
      videos,
      apiClient
    );

    // Ignore any but the most recent request, in case a filter was changed whilst resolving profile images
    if (requestId !== this._currentRequestId) {
      return;
    }

    // Don't update state if the videos are the same as before
    if (
      videos &&
      !!videos.length &&
      prevVideos &&
      _isEqual(videos, prevVideos) &&
      !tabs
    ) {
      return;
    }

    const tabVideos =
      tabs && tabs[tab]
        ? {
            ...tabs,
            [tab]: {
              ...tabs[tab],
              videos: withCreatorProfileImages
            }
          }
        : undefined;

    this.setStateAndPersist((state) => ({
      offset: 0,
      loading: false,
      count,
      videos: withCreatorProfileImages,
      tabs: tabVideos,
      tabMetaData: {
        inDiscussion: {
          ...state.tabMetaData.inDiscussion,
          ...videosInDiscussion
        },
        signOff: {
          ...state.tabMetaData.signOff,
          ...videosInSignOff
        }
      }
    }));
  };

  loadMoreResults = async () => {
    const { getFilteredVideos, videoProjectId, apiClient, product } =
      this.props;
    const {
      searchTerm,
      selectedFilters: selectedFiltersState,
      tabs,
      sortBy,
      videos,
      offset: currOffset,
      count: currCount = 0,
      tab
    } = this.state;
    const selectedFilters = this.applyLockedStatusFilter(
      tabs ? tabs[tab].selectedFilters : selectedFiltersState
    );
    const currentTabVideos = tabs && tabs[tab] ? tabs[tab].videos : undefined;

    if (currOffset + CHUNK_SIZE > currCount) {
      return;
    }

    if (
      (tabs && (!currentTabVideos || !currentTabVideos.length)) ||
      !videos ||
      !videos.length
    ) {
      return;
    }

    const requestId = new Date().getTime();
    this._currentRequestId = requestId;

    const { data, error } = await getFilteredVideos({
      ...selectedFilters,
      searchTerm: tabs ? tabs[tab].searchTerm : searchTerm,
      limit: CHUNK_SIZE,
      offset: currOffset + CHUNK_SIZE,
      sortBy: tabs ? tabs[tab].sortBy : sortBy,
      videoProjectId,
      product: this.isPro ? PRODUCT_TYPE.PRO : product
    });

    // Ignore any but the most recent request
    if (requestId !== this._currentRequestId) {
      return;
    }

    if (!data || error || !data.getFilteredVideos) {
      return void this.setStateAndPersist({ loading: false, error: true });
    }

    const { count, videos: newVideos } = data.getFilteredVideos;

    this.fetchAssetsFromVideos(newVideos);

    const newVideosWithCreatorProfileImages = await resolveProfileImages(
      newVideos,
      apiClient
    );

    // Ignore any but the most recent request, in case a filter was changed whilst resolving profile images
    if (requestId !== this._currentRequestId) {
      return;
    }

    const tabsVideos =
      tabs && tabs[tab]
        ? {
            ...tabs,
            [tab]: {
              ...tabs[tab],
              videos: [
                ...tabs[tab].videos,
                ...newVideosWithCreatorProfileImages
              ]
            }
          }
        : undefined;

    this.setStateAndPersist({
      count,
      videos: [...videos, ...newVideosWithCreatorProfileImages],
      tabs: tabsVideos,
      offset: currOffset + CHUNK_SIZE
    });
  };

  createNewProject = async () => {
    const { history, videoProjectId, product, flags, createVideo } = this.props;

    const { elevateWorkFlowV2 = false } = flags || {};
    const basePath = getBasePathByProductType(product);

    if (elevateWorkFlowV2 && basePath === elevatePath) {
      this.setState({
        isCreateProjectDisabled: true
      });

      const { data, error } = await createVideo({
        name: DEFAULT_PROJECT_NAME,
        projectType: 'PROMO',
        isPrivate: false,
        serviceType: 'SELF',
        product: PRODUCT_TYPE.PITCHTEMPLATE
      });
      if (error || !data || !data.createVideo) {
        notify({ type: 'error' });
        return;
      }

      const { videoId: videoIdValue } = data.createVideo;
      this.setState({
        isCreateProjectDisabled: false
      });
      history.push(`v2/project/${videoIdValue}`);
      return;
    }

    history.rootPush(`${basePath}/builder/new`, { videoProjectId });
  };

  setVideoListRef = (ref: any) => {
    this._dashboardRef = ref;
  };

  getVideoListRef = () =>
    this.props.getInfiniteScrollParent?.() || this._dashboardRef;

  isShowArchived = () => {
    const { selectedFilters, tabs, tab } = this.state;
    const { isArchived = [] } = tabs
      ? tabs[tab].selectedFilters
      : selectedFilters;

    return isArchived.length ? isArchived[0] : false;
  };

  closeTopupCreditModal = () => {
    this.setStateAndPersist({ showTopUpCreditModal: false });
  };

  onHideModal = () => {
    this.setStateAndPersist({ videoToDelete: void 0 });
  };

  closeArchiveModal = () => {
    this.setStateAndPersist({ videoToArchive: void 0 });
  };

  closeRenameModal = () => {
    this.setStateAndPersist({ videoToRename: void 0 });
  };

  closeDuplicateModal = () => {
    this.setStateAndPersist({ videoToDuplicate: void 0 });
  };

  closeCostSummaryModal = () => {
    this.setStateAndPersist({ videoToOpenCostSummary: void 0 });
  };

  closeGetInTouchModal = () => {
    this.setStateAndPersist({ isGetInTouchModalOpen: false });
  };

  closePublicUploadModal = () => {
    this.setStateAndPersist({ videoToPublicUpload: void 0 });
  };

  onOutputArchived = () => {
    const { videoToArchive } = this.state;

    if (!videoToArchive) {
      return;
    }

    const { name } = videoToArchive;

    this.setStateAndPersist(
      {
        videoToArchive: void 0
      },
      () => {
        const action = this.isShowArchived() ? 'unarchived' : 'archived';

        notify({
          content: `${name} successfully ${action}`,
          type: 'success'
        });
        this.fetchData();
      }
    );
  };

  onVideoRenamed = () => {
    const { videoToRename } = this.state;

    if (!videoToRename) {
      return;
    }

    const { name } = videoToRename;

    this.setStateAndPersist(
      {
        videoToRename: void 0
      },
      () => {
        notify({
          content: `${name} successfully renamed`,
          type: 'success'
        });
        this.fetchData();
      }
    );
  };

  onVideoDeleted = () => {
    const { videoToDelete, count, videos, tabs, tab } = this.state;

    const currentTabVideos = tabs && tabs[tab] ? tabs[tab].videos : undefined;

    if (
      (tabs && (!currentTabVideos || !videoToDelete)) ||
      !videos ||
      !videoToDelete
    ) {
      return;
    }

    const { videoId: videoToDeleteId, name } = videoToDelete;
    const newVideos = tabs ? [...currentTabVideos] : [...videos];
    const index = videos.findIndex(
      ({ videoId }) => videoId === videoToDelete.videoId
    );

    newVideos.splice(index, 1);

    const tabsAfterDeletion =
      tabs && tabs[tab]
        ? {
            ...tabs,
            [tab]: {
              ...tabs[tab],
              videos: newVideos
            }
          }
        : undefined;

    this.setStateAndPersist(
      {
        videoToDelete: void 0,
        count: count ? count - 1 : 0,
        videos: newVideos,
        tabs: tabsAfterDeletion
      },
      () => {
        notify({
          content: `${name} successfully deleted.`,
          type: 'success'
        });

        const relatedVideos = newVideos.filter(
          (output) => output.videoId === videoToDeleteId
        );

        if (relatedVideos.length) {
          this.fetchData();
        }
      }
    );
  };

  onVideoDuplicated = () => {
    const { videoToDuplicate } = this.state;

    if (!videoToDuplicate) {
      return;
    }

    const { name } = videoToDuplicate;

    this.setStateAndPersist(
      {
        videoToDuplicate: void 0
      },
      () => {
        notify({
          content: `We've successfully duplicated ${name}.`,
          type: 'success'
        });
        this.fetchData();
      }
    );
  };

  showTopupCreditModal = () => {
    this.setState({
      showTopUpCreditModal: true
    });
  };

  markProjectAsRead = async (video: any) => {
    try {
      const { videoId, organisationId, workflowVersion } = video;
      const { tab } = this.state;

      if (workflowVersion < 5 || tab === 0) return;

      const { createHistoryEntries } = this.props;

      const PROJECT_LISTING_TABS = {
        0: 'ALL',
        1: 'IN_DISCUSSION_PROJECT_READ',
        2: 'SIGN_OFF_PROJECT_READ'
      } as const;

      await createHistoryEntries({
        resourceId: videoId,
        eventType: PROJECT_LISTING_TABS[tab],
        resourceType: 'VIDEO',
        organisationId: organisationId,
        capturedAt: 0
      });
    } catch (error: any) {
      console.error(error);
    }
  };

  handleOpenInvoices = () => {
    const { openInvoices } = this.props;
    const { videoToOpenCostSummary } = this.state;
    const { videoId, organisationId } = videoToOpenCostSummary || {};

    openInvoices(videoId, organisationId, true);
  };

  renderProjectListItem = () => {
    const { product, globalStore, flags, history } = this.props;
    const {
      currentVersionConfig: { listItemComponent: ProjectListItem }
    } = this.state;

    return (video: Video) => (
      <ProjectListItem
        video={video}
        onShare={this.onShareOutput(video)}
        onRename={this.openRenameModal(video)}
        onDelete={this.openDeleteModal(video)}
        onProjectCostSummary={this.openProjectCostSummaryModal(video)}
        onDuplicate={this.openDuplicateModal(video)}
        onPublicUpload={this.openPublicUploadModal(video)}
        isShowArchived={this.isShowArchived()}
        onToggleArchived={this.openArchiveModal(video)}
        product={product}
        globalStore={globalStore}
        flags={flags}
        history={history}
        markProjectAsRead={this.markProjectAsRead}
      />
    );
  };

  renderHelpBanner = () => {
    const { isGettingStartedSteps, videoCountCreatedByUser } = this.state;

    if (videoCountCreatedByUser === undefined) {
      return null;
    }

    const showHelp =
      videoCountCreatedByUser < CURRENT_VIDEOS_LENGTH_WITHOUT_STARTED_STEPS &&
      !isGettingStartedSteps;

    if (showHelp) {
      return (
        <GettingStartedSteps
          onChangeGettingStartedSteps={this.onChangeGettingStartedSteps}
        />
      );
    }

    return <InformationSteps />;
  };

  onShareOutput = (video: Video) => () => {
    this.setStateAndPersist({
      shareableVideo: video,
      newShareModalVisible: true
    });
  };

  onChangeGettingStartedSteps = () => {
    const { isGettingStartedSteps } = this.state;

    this.setStateAndPersist({ isGettingStartedSteps: !isGettingStartedSteps });
  };

  onChangeTab = (e: any) => {
    const { selectedTabIndex } = e;
    this.setState({ tab: selectedTabIndex });
    const { tabs: prevTabs } = this.state;
    const sortBy = prevTabs[selectedTabIndex]
      ? {
          sortBy: prevTabs[selectedTabIndex].sortBy,
          sortByLabel: prevTabs[selectedTabIndex].sortByLabel
        }
      : {};
    this.onSearchFilterChange({
      searchTerm: ''
    });

    this.onSortFiltersChange(sortBy);

    const ProjectsCategory = {
      ALL: 0,
      IN_DISCUSSION: 1,
      SIGN_OFF: 2
    } as const;

    const filterMapping = {
      [ProjectsCategory.ALL]: (state) => {
        const { selectedFilters } = state;
        const updatedFilters = { ...selectedFilters } as const;
        delete updatedFilters.isRequestAssistance;
        delete updatedFilters.STATUS;
        return updatedFilters;
      },
      [ProjectsCategory.IN_DISCUSSION]: () => ({
        isRequestAssistance: true,
        STATUS: ['DRAFT']
      }),
      [ProjectsCategory.SIGN_OFF]: () => ({
        STATUS: ['SIGN_OFF_FINAL']
      })
    } as const;

    this.setStateAndPersist(
      (state) => ({
        ...state,
        selectedFilters: filterMapping[selectedTabIndex](state),
        tabs: {
          ...prevTabs,
          [selectedTabIndex]: {
            ...prevTabs[selectedTabIndex],
            searchTerm: '',
            selectedFilters: filterMapping[selectedTabIndex](state)
          }
        }
      }),
      this.fetchData
    );
  };

  renderTabTitle = (title: string, count, unread) => {
    return (
      <div className="video-list__list-container__tabs--title">
        {unread && (
          <span className="video-list__list-container__tabs--red-dot" />
        )}
        {title}
        {title.toLowerCase() === 'all' ? null : count > 0 && <>({count})</>}
      </div>
    );
  };

  determineSortOptions = (
    tabs,
    tab: any,
    sortByLabel: undefined | string,
    sortBy: undefined | string
  ) => {
    if (tabs && tabs[tab] && tabs[tab].sortBy) {
      return {
        label: tabs[tab].sortByLabel || '',
        value: tabs[tab].sortBy
      };
    }

    if (sortBy) {
      return {
        label: sortByLabel || '',
        value: sortBy
      };
    }

    return this.props?.flags?.workflowV5 ? SORT_FILTERS_V5[0] : SORT_FILTERS[3];
  };

  render() {
    const {
      containerSize,
      history,
      deleteVideo,
      headerTitle,
      headerSubtitle,
      headerIcon,
      headerIconColor,
      headerSecondaryContent,
      headerBreadcrumbs,
      isAdmin,
      subPackage,
      flags,
      organisationMemberships,
      canCreateOrSubmitProject,
      openInvoices,
      canViewOwnedByEveryone,
      clientType,
      selectedOrganisation,
      apiClient,
      lockStatusFilter
    } = this.props;
    const { subEnd = null } = subPackage || {};
    const {
      preferences: { isSignOffWorkflowEnabled }
    } = selectedOrganisation || {};

    const {
      selectedFilters,
      videoToDelete,
      videoToRename,
      videoToDuplicate,
      videoToArchive,
      videoToOpenCostSummary,
      videoToPublicUpload,
      shareableVideo,
      error,
      loading,
      count,
      videos = [],
      isGetInTouchModalOpen,
      users,
      searchTerm,
      tabs,
      sortBy,
      sortByLabel,
      isCreateProjectDisabled,
      currentVersionConfig,
      tab,
      tabMetaData,
      showTopUpCreditModal,
      newShareModalVisible
    } = this.state;

    const {
      inDiscussion: { count: inDiscussionCount, isUnread: inDiscussionUnread },
      signOff: { count: signOffCount, isUnread: signOffUnread }
    } = tabMetaData || {};

    const {
      showShutterstockAssets = false,
      workflowV5 = false,
      enabledPaymentGateway
    } = flags || {};

    if (error) {
      return <Error />;
    }

    const itemType = this.isElevate ? 'project' : 'video';
    const itemTypeCamel = this.isElevate ? 'Project' : 'Video';
    const yourProjectsOrTemplates = 'Your Projects';
    const createProjectLabel = 'Create Project';

    const isShowArchived = this.isShowArchived();
    const createProjectBtnDisabled =
      clientType === 'B2B' &&
      (!canCreateOrSubmitProject || isCreateProjectDisabled);

    const isMedium = containerSize.width < NUMERIC_MEDIA_SIZES.medium + 76;
    const isSmall = containerSize.width < NUMERIC_MEDIA_SIZES.small + 76;
    const numResults = count || 0;
    const selectedFiltersUser = tabs
      ? tabs[tab].selectedFilters.USER
      : selectedFilters.USER;
    const noVideosText = isShowArchived
      ? `There aren't any archived ${itemType}s'}.`
      : `${
          selectedFiltersUser ? 'No one has' : "You haven't"
        } created any videos yet. Use the 'Create ${itemTypeCamel}' to create one.`;

    const now = moment();
    const endDate = moment(subEnd);
    const dayDiff =
      subEnd === null
        ? Infinity
        : Math.round(moment.duration(endDate.diff(now)).asDays());
    const showSubInfo = isAdmin && dayDiff <= 14;

    const secondaryContent = (
      <>
        {headerSecondaryContent}
        {!this.isElevate && !isMedium && (
          <CreditsInfo
            openHubSpotConversationsWidget={openHubSpotConversationsWidget}
            onClickTopUpNow={enabledPaymentGateway && this.showTopupCreditModal}
          />
        )}
      </>
    );

    const { ghost = false } = credentials.get() || {};

    const {
      user: { email }
    } = { user: {}, ...credentials.get() } as const;

    const currentMembership = organisationMemberships.find(
      (membership) => membership.userEmail === email
    );

    const hideCreditsModification = !isShootstaRole(currentMembership);

    const listProps =
      tab === 0
        ? {
            advancedFilters: ADVANCED_FILTERS(
              users,
              canViewOwnedByEveryone,
              lockStatusFilter
            )
          }
        : {};

    const selectedSort = this.determineSortOptions(
      tabs,
      tab,
      sortByLabel,
      sortBy
    );

    const { paymentStatus = '' } = videoToOpenCostSummary || {};

    const hasTabsPermissions = this.getWorkflowV5TabsVisibility();

    return (
      <div className="video-list page" ref={this.setVideoListRef}>
        {showTopUpCreditModal && (
          <TopupCreditsModal
            visible={showTopUpCreditModal}
            onClose={this.closeTopupCreditModal}
          />
        )}
        <ArchiveModal
          visible={Boolean(videoToArchive)}
          video={videoToArchive}
          isShowArchived={isShowArchived}
          onConfirmation={this.onOutputArchived}
          onClose={this.closeArchiveModal}
        />
        <DeleteConfirmationModal
          hideModal={this.onHideModal}
          video={videoToDelete}
          deleteVideo={deleteVideo}
          onComplete={this.onVideoDeleted}
        />
        <RenameConfirmationModal
          visible={Boolean(videoToRename)}
          video={videoToRename}
          onConfirmation={this.onVideoRenamed}
          onClose={this.closeRenameModal}
        />
        <DuplicateModal
          visible={Boolean(videoToDuplicate)}
          video={videoToDuplicate}
          onConfirmation={this.onVideoDuplicated}
          onClose={this.closeDuplicateModal}
        />
        <GetInTouchModal
          product="Pro"
          visible={isGetInTouchModalOpen}
          onClose={this.closeGetInTouchModal}
        />
        <PublicUploadModal
          visible={!!videoToPublicUpload}
          video={videoToPublicUpload}
          onClose={this.closePublicUploadModal}
        />
        {!!videoToOpenCostSummary && (
          <AddonsProvider videoId={videoToOpenCostSummary.videoId}>
            <PaymentSummary
              videoId={videoToOpenCostSummary.videoId}
              videoReadableId={videoToOpenCostSummary.readableId}
              videoName={videoToOpenCostSummary.name}
              videoOutputs={videoToOpenCostSummary.outputs}
              organisationId={videoToOpenCostSummary.organisationId}
              appliedExtras={videoToOpenCostSummary.appliedExtras}
              userSubmittedTeamId={videoToOpenCostSummary.userSubmittedTeamId}
              projectType={videoToOpenCostSummary.projectType}
              onClose={this.closeCostSummaryModal}
              addonsSelector="appliedAddons"
              handlePrint={openInvoices}
              visible
              ghost={ghost}
              hideCreditsModification={hideCreditsModification}
              isShutterStockUnlimited={showShutterstockAssets}
            />
          </AddonsProvider>
        )}
        {!workflowV5 && !this.isElevate && (
          <HomePageHeader
            title={headerTitle}
            actionBtnText={
              flags && flags.hideCreateProject ? undefined : createProjectLabel
            }
            actionBtnOnClick={
              flags && flags.hideCreateProject
                ? undefined
                : this.createNewProject
            }
            actionBtnDisabled={createProjectBtnDisabled}
            secondaryContent={secondaryContent}
            breadCrumbs={headerBreadcrumbs}
            history={history}
          />
        )}
        <div
          className={cx('video-list__list-container column', {
            'video-list__list-container--medium': isMedium
          })}
        >
          <div className="video-list__list-container__list">
            <>
              {shareableVideo && (
                <VideoShareModal
                  videoId={shareableVideo.videoId}
                  visible={newShareModalVisible}
                  onClose={() => this.setState({ newShareModalVisible: false })}
                  apiClient={apiClient}
                  organisationId={shareableVideo.organisationId}
                  shareUrl={getShareUrlForVideo(shareableVideo)}
                  canEdit={hasPermission('UPDATE', shareableVideo?.permissions)}
                />
              )}
              {showSubInfo &&
              this.isPro &&
              !(flags && flags.subscriptionsV22) ? (
                <ElevatedContainer
                  className="video-list__list-container__sub-info"
                  rounded
                >
                  <SubscriptionInfoBox
                    onClick={this.openGetInTouchModal}
                    subscription={{ name: 'Pro', trial: false, endDate }}
                  />
                </ElevatedContainer>
              ) : undefined}
              {this.isPro && !workflowV5 && this.renderHelpBanner()}
              {this.isPro && !workflowV5 && (
                <>
                  <AnalyticsBanner productName="Pro" />
                  <hr />
                  <br />
                </>
              )}
              <ElevatedContainer
                className={cx('video-list__list-container__elevate-container', {
                  'video-list__list-container__elevate-container--workflow-v5':
                    workflowV5
                })}
                rounded
              >
                <div className="video-list__list-container__header">
                  {this.isPro && (
                    <div className="video-list__list-container__header__title">
                      {headerIcon && (
                        <Icon
                          name={headerIcon}
                          color={headerIconColor || 'greenSnake'}
                          size={28}
                        />
                      )}
                      <div className="video-list__list-container__header__title-text">
                        <Text heading3 noMargin="top">
                          {workflowV5
                            ? headerTitle || 'Projects'
                            : yourProjectsOrTemplates}
                        </Text>
                        {headerSubtitle && (
                          <Text subtitle noMargin>
                            {headerSubtitle}
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {workflowV5 &&
                  isSignOffWorkflowEnabled &&
                  !this.isElevate &&
                  !lockStatusFilter && (
                    <div className="video-list__list-container__tabs">
                      <Tabs
                        initialIndex={tab}
                        fixedIndex={tab}
                        updateSelectedTabIndex={this.onChangeTab}
                        className="video-list__list-container__tabs-item"
                        activeBorderPosition="bottom"
                      >
                        <div title={this.renderTabTitle('All', count, false)} />
                        {hasTabsPermissions && [
                          <div
                            key="discussion"
                            title={this.renderTabTitle(
                              'In Discussion',
                              inDiscussionCount,
                              inDiscussionUnread
                            )}
                          />,
                          <div
                            key="signoff"
                            title={this.renderTabTitle(
                              'Sign-off',
                              signOffCount,
                              signOffUnread
                            )}
                          />
                        ]}
                      </Tabs>
                    </div>
                  )}

                <div
                  className={cx('video-list__list-container__projects', {
                    'video-list__list-container__projects--medium': isMedium,
                    'video-list__list-container__projects--workflow-v5':
                      workflowV5
                  })}
                >
                  <List
                    onFiltersChange={this.onSearchFilterChange}
                    listKey="outputId"
                    history={history}
                    data={_uniqBy(tabs ? tabs[tab].videos : videos, 'videoId')}
                    loading={loading}
                    renderCardItem={this.renderProjectListItem()}
                    loadMoreResults={_throttle(this.loadMoreResults, 1000)}
                    totalResults={numResults}
                    getInfiniteScrollParent={this.getVideoListRef}
                    emptyDataText={
                      count
                        ? "There aren't any videos that match your filters."
                        : noVideosText
                    }
                    onAdvancedFiltersChange={this.onUpdateFilters}
                    shouldReturnOptionAllSelections
                    searchTerm={tabs ? tabs[tab].searchTerm : searchTerm}
                    sortFilters={SORT_FILTERS}
                    onSortFiltersChange={this.onSortFiltersChange}
                    selectedSort={selectedSort}
                    isAdvancedFilterPersistent
                    actionButton={
                      (!(flags && flags.hideCreateProject) &&
                        this.isElevate &&
                        isSmall) ||
                      workflowV5
                        ? {
                            isVisible: true,
                            ...(workflowV5 ? { prefix: 'addfat' } : {}),
                            text: workflowV5
                              ? 'New Project'
                              : createProjectLabel,
                            onClick: () => this.createNewProject(),
                            disabled: createProjectBtnDisabled
                          }
                        : undefined
                    }
                    {...(currentVersionConfig.listProps || null)}
                    {...listProps}
                  />
                </div>
              </ElevatedContainer>
            </>
          </div>
        </div>
      </div>
    );
  }
}

export default ProjectList;
